/**
 * QueryVisualModePanel — the Query Editor tab's Visual view: the statement at
 * `statementOffset` rendered as Visual Query Editor cards over the tab's own
 * Monaco buffer (Monaco stays mounted, hidden, underneath).
 *
 * One document, one undo authority: every edit applies through Monaco
 * `executeEdits` (one undo stop each, `useMonacoEditWiring`), and Cmd/Ctrl+Z /
 * Shift+Z step that same Monaco stack while the cards have the keyboard — so
 * typed and visual edits undo in one order. Execution, results, connection and
 * database belong to the Query tab; the panel only asks the host to run the
 * shown statement (Cmd/Ctrl+Enter) and to switch back to SQL.
 */
import { useEffect, useMemo, useRef } from 'react';

import type { DatabaseEngineId } from '../../../../core/engines';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import { useVqeExecuteShortcut, type VqeExecuteSource } from '../execute/useVqeExecuteShortcut';
import { positionAtOffset } from '../inline/inlineModelAdapter';
import { useMonacoEditWiring } from '../inline/useInlineToolbarEditContext';
import { useVqeUndoShortcut } from '../ux/useVqeUndoShortcut';
import { useVqeClassification, vqeParseOptionsFor } from '../workspace/classifyTabQuery';
import { VqeStatementView } from '../workspace/VqeStatementView';

/** SR-Edit document id prefix of a Visual view (`${VQE_DOC_PREFIX}${tabId}`). */
export const VQE_DOC_PREFIX = 'vqe-tab:';

export type QueryVisualModePanelProps = {
  readonly tabId: string;
  readonly editorRef: React.RefObject<SqlMonacoHandle | null>;
  /** The tab's buffer (flushed on every change while the Visual view shows). */
  readonly query: string;
  /** Start offset of the statement to show. */
  readonly statementOffset: number | null;
  readonly engineId: DatabaseEngineId;
  readonly connectionId?: string;
  readonly database?: string;
  readonly isActivePane: boolean;
  /** Take the keyboard when the tab becomes active (not while the AI chat owns it). */
  readonly focusOnActivate: boolean;
  readonly isExecuting: boolean;
  /** The Query tab's destructive-query dialog is open (Enter there must not also run). */
  readonly dangerDialogOpen: boolean;
  readonly onExecute: (source: VqeExecuteSource) => void;
  /** "Run on change" — the same setting the compact popover's footer switch reads/writes (SELECT only). */
  readonly runOnChange: { readonly enabled: boolean; readonly onToggle: () => void };
  readonly onSelectStatement: (startOffset: number) => void;
  /** Switch the tab back to SQL at the shown statement (or at a parse error). */
  readonly onEditAsSql: (errorOffset: number | null) => void;
  /** Open the status bar's database picker. */
  readonly onChooseDatabase: () => void;
  readonly className?: string;
};

export function QueryVisualModePanel({
  tabId,
  editorRef,
  query,
  statementOffset,
  engineId,
  connectionId,
  database,
  isActivePane,
  focusOnActivate,
  isExecuting,
  dangerDialogOpen,
  onExecute,
  runOnChange,
  onSelectStatement,
  onEditAsSql,
  onChooseDatabase,
  className,
}: QueryVisualModePanelProps) {
  const options = useMemo(() => vqeParseOptionsFor(engineId), [engineId]);
  const classification = useVqeClassification(query, options, statementOffset);
  const wiring = useMonacoEditWiring({
    editorRef,
    tabId,
    query,
    statementId: classification?.statement?.id ?? null,
    options,
    connectionId,
    database,
    documentPrefix: VQE_DOC_PREFIX,
  });

  const readSource = () => editorRef.current?.getValue() ?? query;
  const undoEdit = () => editorRef.current?.undo?.() ?? false;
  useVqeUndoShortcut({
    active: isActivePane,
    step: (direction) => (direction === 'undo' ? undoEdit() : (editorRef.current?.redo?.() ?? false)),
  });
  // The cards take the keyboard when the view opens and when the tab becomes
  // active — the hidden editor must never keep receiving keystrokes.
  const rootRef = useRef<HTMLDivElement>(null);
  useVqeExecuteShortcut({ isActivePane, dangerDialogOpen, execute: onExecute, getRoot: () => rootRef.current });
  useEffect(() => {
    if (isActivePane && focusOnActivate) rootRef.current?.focus({ preventScroll: true });
  }, [isActivePane, focusOnActivate]);

  // The starter canvas's seed replaces the (blank) buffer as one undo stop.
  const seedQuery = (sql: string) => {
    const current = readSource();
    const end = positionAtOffset(current, current.length);
    editorRef.current?.executeEdits?.('vqe-seed', [
      { range: { startLineNumber: 1, startColumn: 1, endLineNumber: end.lineNumber, endColumn: end.column }, text: sql },
    ]);
  };

  return (
    <VqeStatementView
      tabId={tabId}
      query={query}
      classification={classification}
      options={options}
      engineId={engineId}
      connectionId={connectionId}
      database={database}
      wiring={wiring}
      isActivePane={isActivePane}
      isExecuting={isExecuting}
      runOnChange={runOnChange}
      documentId={`${VQE_DOC_PREFIX}${tabId}`}
      readSource={readSource}
      undoEdit={undoEdit}
      onSelectStatement={onSelectStatement}
      onEditAsSql={onEditAsSql}
      onChooseDatabase={onChooseDatabase}
      onSeedQuery={seedQuery}
      rootRef={rootRef}
      className={className}
    />
  );
}
